import { motion } from "framer-motion";
import { Star, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useInvalidate } from "@/lib/queries";

export const CompletenessBar = ({ c, className = "" }) => {
  if (!c?.total) return null;
  const color = c.percent >= 100 ? "#10B981" : c.percent >= 50 ? "#F59E0B" : "#EF4444";
  return (
    <div className={`h-1 w-full overflow-hidden rounded-full bg-[#2D3442] ${className}`} title={`${c.filled}/${c.total} recommended fields`} data-testid="completeness-bar">
      <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${c.percent}%`, background: color }} />
    </div>
  );
};

export function CompletenessPanel({ ent, canEdit, onFill }) {
  const invalidate = useInvalidate();
  const c = ent.completeness;
  if (!c || !c.missing?.length) return c?.total ? <p className="mt-6 text-xs text-[#10B981]" data-testid="completeness-complete">All recommended info is filled in.</p> : null;
  const toggle = async (key, on) => {
    await api.post(`/entities/${ent.id}/priority`, { key, on });
    invalidate(["entity", ent.id], ["entities", ent.universe_id]);
  };
  const sorted = [...c.missing].sort((a, b) => b.priority - a.priority || a.optional - b.optional);
  return (
    <motion.section initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-8 rounded-md border border-[#2D3442] bg-[#13171F] p-4" data-testid="completeness-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="lf-label">Missing info · {c.filled}/{c.total} recommended</div>
        {canEdit && <Button size="sm" variant="ghost" className="text-[#F59E0B]" onClick={onFill} data-testid="completeness-fill-button"><PenLine className="h-3.5 w-3.5 mr-1.5" />Fill in</Button>}
      </div>
      <CompletenessBar c={c} className="mt-2 mb-3" />
      <div className="flex flex-wrap gap-1.5">
        {sorted.map((f) => (
          <span key={f.key} className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-1 text-xs ${f.priority ? "border-[#F59E0B]/60 text-[#F59E0B]" : "border-[#2D3442] text-[#A8A090]"} ${f.optional && !f.priority ? "border-dashed opacity-70" : ""}`} data-testid={`missing-field-${f.key}`}>
            {canEdit && (
              <button onClick={() => toggle(f.key, !f.priority)} title={f.priority ? "Remove from priorities" : "Mark as a priority for later"} data-testid={`priority-toggle-${f.key}`}>
                <Star className="h-3.5 w-3.5" fill={f.priority ? "#F59E0B" : "none"} />
              </button>
            )}
            {f.label}{f.optional && " (optional)"}
          </span>
        ))}
      </div>
      {canEdit && <p className="mt-2 text-[11px] text-[#A8A090]">Star a field to mark it as a priority for later.</p>}
    </motion.section>
  );
}
